// eslint-disable-next-line matrix-org/require-copyright-header
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParticipants } from "@livekit/components-react";
import {
  EventTimeline,
  EventType,
  MatrixEventEvent,
  RoomStateEvent,
  type MatrixEvent,
  type Room as MatrixRoom,
} from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";
import {
  DisconnectReason,
  ParticipantEvent,
  RoomEvent,
  type Room,
} from "livekit-client";

import { consumeMeetingCapture } from "../room/meetingCapture";
import type {
  Participant,
  TrackPublication,
  RemoteTrack,
  RemoteTrackPublication,
} from "livekit-client";
import { useClient } from "../ClientContext.tsx";
import {
  RecordingChangeEventType,
  type RecordingChangeAction,
  type RecordingChangeEventContent,
  isRecordingChangeEventContent,
  makeRecordingStateKey,
  parseRecordingStateKeyCallId,
  parseRecordingStateKeyDeviceId,
} from "./recordingChangeEvent.ts";
import { callTraceJournal, describeTraceError } from "../telemetry";

export type LocalRecordingState = "stopped" | "preparing" | "recording";

export type RecordingNotice =
  | { kind: "local_start"; sender: string | null }
  | { kind: "local_stop"; sender: string | null }
  | { kind: "local_connection_lost"; sender: string | null };

export type UseCallRecordingOptions = {
  onFinished?: (blob: Blob) => void;
  onRecordingNotice?: (notice: RecordingNotice) => void;
  mimeType?: string;
};

export type UseCallRecordingResult = {
  localRecordingState: LocalRecordingState;
  localRecordingUserIds: ReadonlySet<string>;
  isLocalRecording: boolean;
  isLocalPreparing: boolean;
  isAnyRecording: boolean;
  isUserLocallyRecording: (userId: string | null | undefined) => boolean;
  error: Error | null;
  startLocalRecording: () => Promise<void>;
  stopLocalRecording: () => Promise<void>;
};

type TrackSourceEntry = {
  source: MediaStreamAudioSourceNode;
  stream: MediaStream;
};

type ParticipantHandlers = {
  trackSubscribed: (track: RemoteTrack, pub: RemoteTrackPublication) => void;
  trackUnsubscribed: (track: RemoteTrack, pub: RemoteTrackPublication) => void;
  trackMuted: (pub: TrackPublication) => void;
  trackUnmuted: (pub: TrackPublication) => void;
};

type ActiveCallBoundaries = {
  callStartedAtTs: number | null;
  deviceJoinedAtTsByKey: Map<string, number>;
  activeDeviceIdsByUserId: Map<string, Set<string>>;
};

// Короткие провалы связи лечатся сами, поэтому не обрываем запись сразу:
// ждём, что соединение вернётся, и только потом сохраняем записанное.
const CONNECTION_LOSS_GRACE_MS = 5000;

const CALL_MEMBERSHIP_EVENT_TYPE = EventType.GroupCallMemberPrefix;
const UNKNOWN_DEVICE_ID = "__legacy_recording_device__";
const DEVICE_KEY_SEPARATOR = "\u0000";

const parseNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const isRecordingEventForCall = (
  event: MatrixEvent,
  content: RecordingChangeEventContent,
  currentCallId: string | null,
): boolean => {
  if (!currentCallId) {
    return true;
  }

  if (content.callId === currentCallId) {
    return true;
  }

  return parseRecordingStateKeyCallId(event.getStateKey()) === currentCallId;
};

const parseCallMembershipCreatedTs = (event: MatrixEvent): number | null => {
  const content = event.getContent() as Record<string, unknown> | null;
  if (!content || typeof content !== "object") {
    return null;
  }

  if (Object.keys(content).length === 0) {
    // Пустое содержимое state-event означает leave для m.call.member.
    return null;
  }

  if (content.application !== "m.call") {
    return null;
  }

  if (typeof content.call_id !== "string") {
    return null;
  }

  if (content.scope !== undefined && content.scope !== "m.room") {
    return null;
  }

  const createdTs =
    typeof content.created_ts === "number" &&
    Number.isFinite(content.created_ts)
      ? Math.floor(content.created_ts)
      : event.getTs();

  if (typeof content.expires === "number" && Number.isFinite(content.expires)) {
    if (createdTs + content.expires <= Date.now()) {
      return null;
    }
  }

  return createdTs;
};

const parseCallMembershipDeviceId = (event: MatrixEvent): string | null => {
  const content = event.getContent() as Record<string, unknown> | null;
  if (!content || typeof content !== "object") {
    // Продолжаем разбор через state_key: leave-ивенты приходят с пустым content.
  } else {
    const deviceId = parseNonEmptyString(content.device_id);
    if (deviceId) {
      return deviceId;
    }
  }

  const sender = event.getSender();
  const stateKey = parseNonEmptyString(event.getStateKey());
  if (!sender || !stateKey) {
    return null;
  }

  // Формат state_key: [_]{userId}_{deviceId}_m.call{callId}
  // Для room versions с ограниченными auth rules может быть ведущий "_".
  const normalizedStateKey = stateKey.startsWith("_")
    ? stateKey.slice(1)
    : stateKey;
  const senderPrefix = `${sender}_`;
  if (!normalizedStateKey.startsWith(senderPrefix)) {
    return null;
  }

  const callMarkerIndex = normalizedStateKey.lastIndexOf("_m.call");
  if (callMarkerIndex <= senderPrefix.length) {
    return null;
  }

  const deviceId = normalizedStateKey.slice(
    senderPrefix.length,
    callMarkerIndex,
  );
  return deviceId.length > 0 ? deviceId : null;
};

const makeUserDeviceKey = (userId: string, deviceId: string): string =>
  `${userId}${DEVICE_KEY_SEPARATOR}${deviceId}`;

const parseUserIdFromDeviceKey = (key: string): string => {
  const separatorIndex = key.indexOf(DEVICE_KEY_SEPARATOR);
  return separatorIndex >= 0 ? key.slice(0, separatorIndex) : key;
};

const getUserIdFromParticipantIdentity = (
  identity: string | undefined,
): string | null => {
  if (!identity) {
    return null;
  }

  const separatorIndex = identity.lastIndexOf(":");
  if (separatorIndex < 0) {
    return null;
  }

  const userId = identity.slice(0, separatorIndex);
  return userId.startsWith("@") ? userId : null;
};

/**
 * Хук управления локальной записью звонка.
 */
export function useCallRecording(
  room: Room,
  matrixRoom: MatrixRoom,
  options: UseCallRecordingOptions = {},
): UseCallRecordingResult {
  const cli = useClient();
  const {
    onFinished,
    onRecordingNotice,
    mimeType = "video/webm;codecs=vp9,opus",
  } = options;
  const participants = useParticipants({ room });

  const onFinishedRef = useRef<UseCallRecordingOptions["onFinished"]>(null);
  onFinishedRef.current = onFinished;

  const onRecordingNoticeRef =
    useRef<UseCallRecordingOptions["onRecordingNotice"]>(null);
  onRecordingNoticeRef.current = onRecordingNotice;

  const [localRecordingState, setLocalRecordingState] =
    useState<LocalRecordingState>("stopped");
  const [localRecordingDeviceKeys, setLocalRecordingDeviceKeys] = useState<
    Set<string>
  >(new Set());
  const [error, setError] = useState<Error | null>(null);
  const recordingRequest = useRef(0);

  const isLocalRecording = localRecordingState === "recording";
  const isLocalPreparing = localRecordingState === "preparing";
  const isAnyRecording = isLocalRecording;

  const currentUserId = cli.client?.getUserId() ?? null;
  const currentDeviceId = cli.client?.getDeviceId() ?? null;

  const recorderRef = useRef<MediaRecorder | null>(null);
  const tabStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const recordingMimeTypeRef = useRef<string>("video/webm");
  const recordingDeliveredRef = useRef(false);

  const processedRecordingEventsRef = useRef<Set<string>>(new Set());
  const latestJoinTsByDeviceKeyRef = useRef<Map<string, number>>(new Map());
  const activeDeviceIdsByUserRef = useRef<Map<string, Set<string>>>(new Map());
  const localTimelineSyncGenerationRef = useRef(0);

  // key = `${participantSid}:${trackSid}`
  const trackSourcesRef = useRef<Map<string, TrackSourceEntry>>(new Map());

  // key = participantSid
  const participantHandlersRef = useRef<
    Map<string, { participant: Participant; handlers: ParticipantHandlers }>
  >(new Map());

  const makeKey = (participantSid: string, trackSid: string): string =>
    `${participantSid}:${trackSid}`;

  const inCallUserIds = useMemo(() => {
    const users = new Set<string>();

    if (currentUserId) {
      users.add(currentUserId);
    }

    participants.forEach((participant) => {
      const userId = getUserIdFromParticipantIdentity(participant.identity);
      if (userId) {
        users.add(userId);
      }
    });

    return users;
  }, [participants, currentUserId]);

  const inCallUserIdsSignature = useMemo(
    () => Array.from(inCallUserIds).sort().join("|"),
    [inCallUserIds],
  );

  const emitRecordingNotice = useCallback((notice: RecordingNotice): void => {
    onRecordingNoticeRef.current?.(notice);
  }, []);

  const setDeviceLocalRecording = useCallback(
    (
      userId: string | null,
      deviceId: string | null,
      isRecordingNow: boolean,
    ): void => {
      if (!userId || !deviceId) {
        return;
      }

      const deviceKey = makeUserDeviceKey(userId, deviceId);
      setLocalRecordingDeviceKeys((prev) => {
        const next = new Set(prev);
        if (isRecordingNow) {
          next.add(deviceKey);
        } else {
          next.delete(deviceKey);
        }
        return next;
      });
    },
    [],
  );

  const setUserLocalRecordingFallback = useCallback(
    (userId: string | null, isRecordingNow: boolean): void => {
      if (!userId) {
        return;
      }

      const activeDevices = activeDeviceIdsByUserRef.current.get(userId);
      if (activeDevices && activeDevices.size > 0) {
        activeDevices.forEach((deviceId) => {
          setDeviceLocalRecording(userId, deviceId, isRecordingNow);
        });
        return;
      }

      // Для старых событий без deviceId используем синтетический девайс.
      setDeviceLocalRecording(userId, UNKNOWN_DEVICE_ID, isRecordingNow);
    },
    [setDeviceLocalRecording],
  );

  const resolveActiveCallBoundaries = useCallback((): ActiveCallBoundaries => {
    const result: ActiveCallBoundaries = {
      callStartedAtTs: null,
      deviceJoinedAtTsByKey: new Map<string, number>(),
      activeDeviceIdsByUserId: new Map<string, Set<string>>(),
    };

    const roomState = matrixRoom
      .getLiveTimeline()
      .getState(EventTimeline.FORWARDS);
    if (!roomState) {
      return result;
    }

    const membershipEvents = roomState.getStateEvents(
      CALL_MEMBERSHIP_EVENT_TYPE,
    );
    for (const event of membershipEvents) {
      const sender = event.getSender();
      if (!sender) {
        continue;
      }

      const joinedAtTs = parseCallMembershipCreatedTs(event);
      if (joinedAtTs === null) {
        continue;
      }

      if (
        result.callStartedAtTs === null ||
        joinedAtTs < result.callStartedAtTs
      ) {
        result.callStartedAtTs = joinedAtTs;
      }

      if (!inCallUserIds.has(sender)) {
        continue;
      }

      const deviceId = parseCallMembershipDeviceId(event);
      if (!deviceId) {
        continue;
      }

      const deviceKey = makeUserDeviceKey(sender, deviceId);
      const previousJoinedAtTs = result.deviceJoinedAtTsByKey.get(deviceKey);
      if (previousJoinedAtTs === undefined || joinedAtTs > previousJoinedAtTs) {
        result.deviceJoinedAtTsByKey.set(deviceKey, joinedAtTs);
      }

      const knownDeviceIds =
        result.activeDeviceIdsByUserId.get(sender) ?? new Set();
      knownDeviceIds.add(deviceId);
      result.activeDeviceIdsByUserId.set(sender, knownDeviceIds);
    }

    return result;
  }, [inCallUserIds, matrixRoom]);

  const resolveRoomCallId = useCallback(async (): Promise<string | null> => {
    return (
      parseNonEmptyString(room.name) ??
      parseNonEmptyString(await room.getSid().catch(() => null))
    );
  }, [room]);

  const sendRecordingChangeEvent = useCallback(
    async ({
      action,
      deviceId,
    }: {
      action: RecordingChangeAction;
      deviceId?: string | null;
    }): Promise<void> => {
      const matrixClient = cli.client;
      if (!matrixClient) {
        return;
      }

      const roomCallId = await resolveRoomCallId();
      if (!roomCallId) {
        logger.warn(
          "[Recorder] Не удалось определить callId для события записи",
        );
        return;
      }

      const content: RecordingChangeEventContent = {
        action,
        type: "local",
        callId: roomCallId,
        deviceId: deviceId ?? null,
      };
      const senderUserId = matrixClient.getUserId();
      const effectiveDeviceId = parseNonEmptyString(deviceId);
      if (!senderUserId || !effectiveDeviceId) {
        logger.warn(
          "[Recorder] Не удалось определить sender/device для state-события записи",
        );
        return;
      }

      const stateKey = makeRecordingStateKey(
        senderUserId,
        effectiveDeviceId,
        roomCallId,
      );

      try {
        const sendCustomStateEvent = matrixClient.sendStateEvent as (
          roomId: string,
          eventType: string,
          content: object,
          stateKey?: string,
        ) => Promise<unknown>;

        await sendCustomStateEvent(
          matrixRoom.roomId,
          RecordingChangeEventType,
          content,
          stateKey,
        );
      } catch (err) {
        logger.warn("[Recorder] Не удалось отправить событие записи", err);
      }
    },
    [cli.client, matrixRoom.roomId, resolveRoomCallId],
  );

  const readLocalRecordingDevicesFromState = useCallback(
    async (
      boundaries: ActiveCallBoundaries = resolveActiveCallBoundaries(),
    ): Promise<Set<string>> => {
      const matrixClient = cli.client;
      if (!matrixClient) {
        return new Set();
      }

      const currentCallId = await resolveRoomCallId();
      const stateEvents = matrixRoom.currentState.getStateEvents(
        RecordingChangeEventType,
      );
      if (!Array.isArray(stateEvents) || stateEvents.length === 0) {
        return new Set();
      }

      const recordingDeviceKeys = new Set<string>();

      for (const event of stateEvents) {
        await matrixClient.decryptEventIfNeeded(event).catch(() => {});
        if (event.isBeingDecrypted() || event.isDecryptionFailure()) {
          continue;
        }

        const content = event.getContent();
        if (!isRecordingChangeEventContent(content)) {
          continue;
        }

        if (!isRecordingEventForCall(event, content, currentCallId)) {
          continue;
        }

        const sender = event.getSender();
        if (!sender) {
          continue;
        }

        const eventDeviceId =
          parseNonEmptyString(content.deviceId) ??
          parseRecordingStateKeyDeviceId(sender, event.getStateKey());
        if (!eventDeviceId) {
          continue;
        }

        const eventDeviceKey = makeUserDeviceKey(sender, eventDeviceId);
        const joinedAtTs = boundaries.deviceJoinedAtTsByKey.get(eventDeviceKey);
        if (joinedAtTs === undefined || event.getTs() < joinedAtTs) {
          continue;
        }

        if (content.action === "start") {
          recordingDeviceKeys.add(eventDeviceKey);
        } else {
          recordingDeviceKeys.delete(eventDeviceKey);
        }
      }

      return recordingDeviceKeys;
    },
    [
      cli.client,
      matrixRoom.currentState,
      resolveActiveCallBoundaries,
      resolveRoomCallId,
    ],
  );

  const reconcileOwnRecordingState = useCallback(
    async (recordingDeviceKeys: Set<string>): Promise<Set<string>> => {
      if (!currentUserId || !currentDeviceId) {
        return recordingDeviceKeys;
      }

      const currentDeviceKey = makeUserDeviceKey(
        currentUserId,
        currentDeviceId,
      );
      const isStateRecording = recordingDeviceKeys.has(currentDeviceKey);
      if (!isStateRecording || localRecordingState !== "stopped") {
        return recordingDeviceKeys;
      }

      await sendRecordingChangeEvent({
        action: "stop",
        deviceId: currentDeviceId,
      });

      const nextRecordingDeviceKeys = new Set(recordingDeviceKeys);
      nextRecordingDeviceKeys.delete(currentDeviceKey);
      return nextRecordingDeviceKeys;
    },
    [
      currentDeviceId,
      currentUserId,
      localRecordingState,
      sendRecordingChangeEvent,
    ],
  );

  const detachTrack = useCallback(
    (participantSid: string, trackSid: string) => {
      const key = makeKey(participantSid, trackSid);
      const entry = trackSourcesRef.current.get(key);
      if (!entry) return;

      try {
        entry.source.disconnect();
      } catch {
        // Ошибка отключения уже удаляемого источника не требует обработки.
      }

      trackSourcesRef.current.delete(key);
    },
    [],
  );

  const attachTrack = useCallback(
    (participant: Participant, publication: TrackPublication) => {
      const ctx = audioContextRef.current;
      const dest = destinationRef.current;
      if (!ctx || !dest) return;

      const track = publication.track;
      if (!track?.mediaStreamTrack) return;
      if (track.mediaStreamTrack.kind !== "audio") return;
      if (publication.isMuted) return;

      const key = makeKey(participant.sid, publication.trackSid);

      // Если уже был источник, отключаем его, чтобы не получить дубли в миксе.
      const existing = trackSourcesRef.current.get(key);
      if (existing) {
        try {
          existing.source.disconnect();
        } catch {
          // ignore
        }
        trackSourcesRef.current.delete(key);
      }

      const stream = new MediaStream([track.mediaStreamTrack]);
      const source = ctx.createMediaStreamSource(stream);
      source.connect(dest);

      trackSourcesRef.current.set(key, { source, stream });
    },
    [],
  );

  const detachAllTracksForParticipant = useCallback(
    (participantSid: string) => {
      const keys = Array.from(trackSourcesRef.current.keys()).filter((k) =>
        k.startsWith(`${participantSid}:`),
      );
      keys.forEach((k) => {
        const entry = trackSourcesRef.current.get(k);
        if (!entry) return;

        try {
          entry.source.disconnect();
        } catch {
          // ignore
        }

        trackSourcesRef.current.delete(k);
      });
    },
    [],
  );

  const bindParticipantEvents = useCallback(
    (participant: Participant) => {
      if (participantHandlersRef.current.has(participant.sid)) return;

      const handlers: ParticipantHandlers = {
        trackSubscribed: (_track: RemoteTrack, pub: RemoteTrackPublication) => {
          attachTrack(participant, pub);
        },
        trackUnsubscribed: (
          _track: RemoteTrack,
          pub: RemoteTrackPublication,
        ) => {
          detachTrack(participant.sid, pub.trackSid);
        },
        trackMuted: (pub: TrackPublication) => {
          detachTrack(participant.sid, pub.trackSid);
        },
        trackUnmuted: (pub: TrackPublication) => {
          attachTrack(participant, pub);
        },
      };

      participant.on(
        ParticipantEvent.TrackSubscribed,
        handlers.trackSubscribed,
      );
      participant.on(
        ParticipantEvent.TrackUnsubscribed,
        handlers.trackUnsubscribed,
      );
      participant.on(ParticipantEvent.TrackMuted, handlers.trackMuted);
      participant.on(ParticipantEvent.TrackUnmuted, handlers.trackUnmuted);

      participantHandlersRef.current.set(participant.sid, {
        participant,
        handlers,
      });
    },
    [attachTrack, detachTrack],
  );

  const unbindParticipantEvents = useCallback((sid: string) => {
    const entry = participantHandlersRef.current.get(sid);
    if (!entry) return;

    const { participant, handlers } = entry;

    participant.off(ParticipantEvent.TrackSubscribed, handlers.trackSubscribed);
    participant.off(
      ParticipantEvent.TrackUnsubscribed,
      handlers.trackUnsubscribed,
    );
    participant.off(ParticipantEvent.TrackMuted, handlers.trackMuted);
    participant.off(ParticipantEvent.TrackUnmuted, handlers.trackUnmuted);

    participantHandlersRef.current.delete(sid);
  }, []);

  const attachExistingTracks = useCallback(
    (participant: Participant) => {
      participant.audioTrackPublications.forEach((pub) => {
        attachTrack(participant, pub as TrackPublication);
      });
    },
    [attachTrack],
  );

  const cleanup = useCallback(() => {
    recordingRequest.current += 1;
    recorderRef.current = null;
    // Куски записи здесь не трогаем: MediaRecorder отдаёт последнюю порцию и
    // событие остановки уже после cleanup, а раннее обнуление стирало всё
    // записанное — человек оставался без файла.

    tabStreamRef.current?.getTracks().forEach((track) => track.stop());
    tabStreamRef.current = null;

    trackSourcesRef.current.forEach((entry) => {
      try {
        entry.source.disconnect();
      } catch {
        // ignore
      }
    });
    trackSourcesRef.current.clear();

    participantHandlersRef.current.forEach((_entry, sid) => {
      unbindParticipantEvents(sid);
      detachAllTracksForParticipant(sid);
    });
    participantHandlersRef.current.clear();

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    destinationRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Отдаёт человеку то, что успело записаться. Возвращает размер файла.
   */
  const deliverRecordedChunks = useCallback((): number => {
    // Обычная остановка, ошибка записи и обрыв связи могут сработать вместе,
    // а файл человеку нужен один — поэтому отдаём ровно один раз.
    if (recordingDeliveredRef.current) {
      return 0;
    }
    recordingDeliveredRef.current = true;

    const chunks = chunksRef.current;
    chunksRef.current = [];
    if (chunks.length === 0) {
      return 0;
    }

    const blob = new Blob(chunks, { type: recordingMimeTypeRef.current });
    if (blob.size === 0) {
      return 0;
    }

    onFinishedRef.current?.(blob);
    return blob.size;
  }, []);

  /**
   * Завершает запись из любого состояния и гарантирует сохранение записанного.
   */
  const finishRecording = useCallback((): void => {
    const recorder = recorderRef.current;

    if (recorder && recorder.state !== "inactive") {
      try {
        callTraceJournal.record({
          eventType: "recording.local_stop_requested",
          stage: "recording",
          outcome: "started",
        });
        // Дальше файл соберёт обработчик остановки: только он видит
        // последнюю порцию данных, которую MediaRecorder отдаёт при stop().
        recorder.stop();
        return;
      } catch (error) {
        callTraceJournal.record({
          eventType: "recording.local_stop_failed",
          stage: "recording",
          severity: "error",
          outcome: "failed",
          payload: describeTraceError(error),
        });
      }
    }

    // Записывающий уже мёртв (ошибка, закрытая вкладка-источник) — сохраняем
    // накопленное сами, иначе оно так и останется в памяти.
    deliverRecordedChunks();
    cleanup();
    setLocalRecordingState("stopped");
    setDeviceLocalRecording(
      currentUserId,
      currentDeviceId ?? UNKNOWN_DEVICE_ID,
      false,
    );
  }, [
    cleanup,
    currentDeviceId,
    currentUserId,
    deliverRecordedChunks,
    setDeviceLocalRecording,
  ]);

  const finishRecordingRef = useRef(finishRecording);
  finishRecordingRef.current = finishRecording;

  const stopLocalRecording = useCallback(async (): Promise<void> => {
    if (localRecordingState === "preparing") {
      cleanup();
      setLocalRecordingState("stopped");
      return;
    }
    if (localRecordingState !== "recording") {
      return Promise.resolve();
    }

    const recorder = recorderRef.current;
    if (!recorder) {
      return Promise.resolve();
    }

    if (recorder.state === "inactive") {
      return Promise.resolve();
    }

    try {
      callTraceJournal.record({
        eventType: "recording.local_stop_requested",
        stage: "recording",
        outcome: "started",
      });
      recorder.stop();
    } catch (error) {
      callTraceJournal.record({
        eventType: "recording.local_stop_failed",
        stage: "recording",
        severity: "error",
        outcome: "failed",
        payload: describeTraceError(error),
      });
    }
  }, [localRecordingState, cleanup]);

  const startLocalRecording = useCallback(async (): Promise<void> => {
    if (localRecordingState !== "stopped") {
      return;
    }

    const request = ++recordingRequest.current;
    setError(null);
    setLocalRecordingState("preparing");
    const startEventId = callTraceJournal.record({
      eventType: "recording.local_start_requested",
      stage: "recording",
      outcome: "started",
    });
    const recordingStartedAt = performance.now();
    let failureOperation: "recorder_setup" | "recording_state_event" =
      "recorder_setup";

    try {
      const tabStream = await consumeMeetingCapture(matrixRoom.roomId);
      if (request !== recordingRequest.current) {
        tabStream.getTracks().forEach((track) => track.stop());
        return;
      }
      tabStreamRef.current = tabStream;

      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      audioContextRef.current = ctx;
      destinationRef.current = dest;

      const mixedStream = new MediaStream([
        ...tabStream.getVideoTracks(),
        ...dest.stream.getAudioTracks(),
      ]);

      const supportedMimeType = MediaRecorder.isTypeSupported(mimeType)
        ? mimeType
        : "video/webm";

      const recorder = new MediaRecorder(mixedStream, {
        mimeType: supportedMimeType,
      });
      recorderRef.current = recorder;
      chunksRef.current = [];
      recordingMimeTypeRef.current = supportedMimeType;
      recordingDeliveredRef.current = false;

      recorder.ondataavailable = (e: BlobEvent): void => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onerror = (e): void => {
        callTraceJournal.record({
          eventType: "recording.local_start_failed",
          stage: "recording",
          severity: "error",
          outcome: "failed",
          reasonCode: "media_recorder_error",
          payload: {
            operation: "media_recorder_event",
            recorderState: recorder.state,
            ...describeTraceError(e.error),
          },
        });
        setError(e.error || new Error("Recording error"));
        // Сбой записи — не повод выбрасывать уже записанное.
        finishRecordingRef.current();
      };

      recorder.onstop = (): void => {
        const savedBytes = deliverRecordedChunks();
        cleanup();
        setLocalRecordingState("stopped");
        callTraceJournal.record({
          eventType: "recording.local_stopped",
          stage: "recording",
          outcome: "succeeded",
          payload: { blobBytes: savedBytes },
        });
        setDeviceLocalRecording(
          currentUserId,
          currentDeviceId ?? UNKNOWN_DEVICE_ID,
          false,
        );

        void sendRecordingChangeEvent({
          action: "stop",
          deviceId: currentDeviceId,
        });
      };

      participants.forEach((participant) => {
        attachExistingTracks(participant);
        bindParticipantEvents(participant);
      });

      recorder.start(1000);
      setLocalRecordingState("recording");
      callTraceJournal.record({
        eventType: "recording.local_started",
        stage: "recording",
        outcome: "succeeded",
        relatedEventId: startEventId,
        durationMs: Math.round(performance.now() - recordingStartedAt),
        payload: { mimeType: supportedMimeType },
      });
      setDeviceLocalRecording(
        currentUserId,
        currentDeviceId ?? UNKNOWN_DEVICE_ID,
        true,
      );

      failureOperation = "recording_state_event";
      await sendRecordingChangeEvent({
        action: "start",
        deviceId: currentDeviceId,
      });
    } catch (err) {
      if (request !== recordingRequest.current) return;
      callTraceJournal.record({
        eventType: "recording.local_start_failed",
        stage: "recording",
        severity: "error",
        outcome: "failed",
        relatedEventId: startEventId,
        durationMs: Math.round(performance.now() - recordingStartedAt),
        payload: {
          operation: failureOperation,
          recorderState: recorderRef.current?.state ?? "unavailable",
          ...describeTraceError(err),
        },
      });
      setError(
        err instanceof Error ? err : new Error("Failed to start recording"),
      );
      setLocalRecordingState("stopped");
      setDeviceLocalRecording(
        currentUserId,
        currentDeviceId ?? UNKNOWN_DEVICE_ID,
        false,
      );
      cleanup();
    }
  }, [
    attachExistingTracks,
    bindParticipantEvents,
    cleanup,
    currentDeviceId,
    currentUserId,
    deliverRecordedChunks,
    localRecordingState,
    matrixRoom.roomId,
    mimeType,
    participants,
    sendRecordingChangeEvent,
    setDeviceLocalRecording,
  ]);

  const handleRecordingChangeEvent = useCallback(
    async (event: MatrixEvent): Promise<void> => {
      if (event.getRoomId() !== matrixRoom.roomId || event.isSending()) {
        return;
      }

      const eventId = event.getId();
      if (eventId && processedRecordingEventsRef.current.has(eventId)) {
        return;
      }

      const matrixClient = cli.client;
      if (!matrixClient) {
        return;
      }

      await matrixClient.decryptEventIfNeeded(event).catch(() => {});
      if (event.isBeingDecrypted() || event.isDecryptionFailure()) {
        return;
      }

      const eventType = event.getType();

      if (eventType === CALL_MEMBERSHIP_EVENT_TYPE) {
        const sender = event.getSender();
        if (!sender) {
          return;
        }

        const membershipDeviceId = parseCallMembershipDeviceId(event);
        if (!membershipDeviceId) {
          return;
        }

        const deviceKey = makeUserDeviceKey(sender, membershipDeviceId);
        const joinedAtTs = parseCallMembershipCreatedTs(event);
        if (joinedAtTs === null) {
          latestJoinTsByDeviceKeyRef.current.delete(deviceKey);

          const activeDeviceIds = activeDeviceIdsByUserRef.current.get(sender);
          if (activeDeviceIds) {
            activeDeviceIds.delete(membershipDeviceId);
            if (activeDeviceIds.size === 0) {
              activeDeviceIdsByUserRef.current.delete(sender);
            } else {
              activeDeviceIdsByUserRef.current.set(sender, activeDeviceIds);
            }
          }

          setDeviceLocalRecording(sender, membershipDeviceId, false);
          if (!activeDeviceIdsByUserRef.current.has(sender)) {
            setDeviceLocalRecording(sender, UNKNOWN_DEVICE_ID, false);
          }
          return;
        }

        const activeDeviceIds =
          activeDeviceIdsByUserRef.current.get(sender) ?? new Set();
        activeDeviceIds.add(membershipDeviceId);
        activeDeviceIdsByUserRef.current.set(sender, activeDeviceIds);

        const previousJoinedAtTs =
          latestJoinTsByDeviceKeyRef.current.get(deviceKey);
        if (
          previousJoinedAtTs === undefined ||
          joinedAtTs > previousJoinedAtTs
        ) {
          latestJoinTsByDeviceKeyRef.current.set(deviceKey, joinedAtTs);
          setDeviceLocalRecording(sender, membershipDeviceId, false);
          setDeviceLocalRecording(sender, UNKNOWN_DEVICE_ID, false);
        }

        return;
      }

      if (eventType !== RecordingChangeEventType) {
        return;
      }

      const content = event.getContent();
      if (!isRecordingChangeEventContent(content)) {
        return;
      }

      const currentCallId = await resolveRoomCallId();
      if (!isRecordingEventForCall(event, content, currentCallId)) {
        return;
      }
      callTraceJournal.record({
        eventType: "recording.remote_state_observed",
        stage: "recording",
        outcome: "state_changed",
        payload: {
          action: content.action,
          type: content.type,
          isLocalUser: event.getSender() === currentUserId,
        },
      });

      if (eventId) {
        processedRecordingEventsRef.current.add(eventId);
        if (processedRecordingEventsRef.current.size > 500) {
          processedRecordingEventsRef.current.clear();
        }
      }

      const sender = event.getSender() ?? null;
      const eventDeviceId =
        parseNonEmptyString(content.deviceId) ??
        parseRecordingStateKeyDeviceId(sender, event.getStateKey());
      const isStart = content.action === "start";

      if (content.action === "start") {
        emitRecordingNotice({ kind: "local_start", sender });
      } else {
        emitRecordingNotice({ kind: "local_stop", sender });
      }

      if (eventDeviceId) {
        setDeviceLocalRecording(sender, eventDeviceId, isStart);
      } else {
        setUserLocalRecordingFallback(sender, isStart);
      }
    },
    [
      cli.client,
      currentUserId,
      emitRecordingNotice,
      matrixRoom.roomId,
      resolveRoomCallId,
      setDeviceLocalRecording,
      setUserLocalRecordingFallback,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const generation = localTimelineSyncGenerationRef.current + 1;
    localTimelineSyncGenerationRef.current = generation;

    const boundaries = resolveActiveCallBoundaries();
    latestJoinTsByDeviceKeyRef.current = new Map(
      boundaries.deviceJoinedAtTsByKey,
    );
    activeDeviceIdsByUserRef.current = new Map(
      Array.from(boundaries.activeDeviceIdsByUserId.entries()).map(
        ([userId, deviceIds]) => [userId, new Set(deviceIds)],
      ),
    );

    void readLocalRecordingDevicesFromState(boundaries)
      .then(async (recordingDeviceKeysFromState) =>
        reconcileOwnRecordingState(recordingDeviceKeysFromState),
      )
      .then((recordingDeviceKeys) => {
        if (
          cancelled ||
          generation !== localTimelineSyncGenerationRef.current
        ) {
          return;
        }
        if (isLocalRecording && currentUserId) {
          recordingDeviceKeys.add(
            makeUserDeviceKey(
              currentUserId,
              currentDeviceId ?? UNKNOWN_DEVICE_ID,
            ),
          );
        }
        setLocalRecordingDeviceKeys(recordingDeviceKeys);
      })
      .catch((err) => {
        logger.warn(
          "[Recorder] Не удалось восстановить локальные записи из state",
          err,
        );
      });

    return (): void => {
      cancelled = true;
    };
  }, [
    currentDeviceId,
    currentUserId,
    inCallUserIdsSignature,
    isLocalRecording,
    reconcileOwnRecordingState,
    readLocalRecordingDevicesFromState,
    resolveActiveCallBoundaries,
  ]);

  useEffect(() => {
    const matrixClient = cli.client;
    if (!matrixClient) {
      return;
    }

    const onStateEvent = (
      event: MatrixEvent,
      _state: unknown,
      _prevEvent: MatrixEvent | null,
    ): void => {
      void handleRecordingChangeEvent(event);
    };

    const onDecryptedEvent = (event: MatrixEvent): void => {
      void handleRecordingChangeEvent(event);
    };

    matrixRoom.currentState.on(RoomStateEvent.Events, onStateEvent);
    matrixClient.on(MatrixEventEvent.Decrypted, onDecryptedEvent);

    return (): void => {
      matrixRoom.currentState.off(RoomStateEvent.Events, onStateEvent);
      matrixClient.off(MatrixEventEvent.Decrypted, onDecryptedEvent);
    };
  }, [cli.client, handleRecordingChangeEvent, matrixRoom.currentState]);

  useEffect(() => {
    if (!isLocalRecording) return;

    participants.forEach((participant) => {
      if (!participantHandlersRef.current.has(participant.sid)) {
        attachExistingTracks(participant);
        bindParticipantEvents(participant);
      }
    });

    participantHandlersRef.current.forEach((_entry, sid) => {
      const stillHere = participants.some(
        (participant) => participant.sid === sid,
      );
      if (!stillHere) {
        unbindParticipantEvents(sid);
        detachAllTracksForParticipant(sid);
      }
    });
  }, [
    participants,
    isLocalRecording,
    attachExistingTracks,
    bindParticipantEvents,
    detachAllTracksForParticipant,
    unbindParticipantEvents,
  ]);

  useEffect(() => {
    return (): void => {
      // Выход из звонка раньше рвал дорожки и чистил память, не дождавшись
      // файла. Теперь уходим через завершение записи — оно сохранит записанное.
      finishRecordingRef.current();
    };
    // Эффект нужен только на unmount, ссылка на завершение живёт в ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLocalRecording) {
      return;
    }

    const close = (): void => {
      finishRecordingRef.current();
    };

    window.addEventListener("beforeunload", close);
    return (): void => {
      window.removeEventListener("beforeunload", close);
    };
    // При смене статуса локальной записи переустанавливаем обработчик.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalRecording]);

  useEffect(() => {
    if (!isLocalRecording) {
      return;
    }

    let graceTimeoutId: number | null = null;

    const clearGrace = (): void => {
      if (graceTimeoutId !== null) {
        window.clearTimeout(graceTimeoutId);
        graceTimeoutId = null;
      }
    };

    const finishBecauseConnectionLost = (): void => {
      clearGrace();
      emitRecordingNotice({
        kind: "local_connection_lost",
        sender: currentUserId,
      });
      finishRecordingRef.current();
    };

    const onOffline = (): void => {
      if (graceTimeoutId !== null) {
        return;
      }
      graceTimeoutId = window.setTimeout(
        finishBecauseConnectionLost,
        CONNECTION_LOSS_GRACE_MS,
      );
    };

    const onOnline = (): void => {
      clearGrace();
    };

    const onDisconnected = (reason?: DisconnectReason): void => {
      // Штатный выход из звонка обрабатывается обычной остановкой записи.
      if (reason === DisconnectReason.CLIENT_INITIATED) {
        return;
      }
      finishBecauseConnectionLost();
    };

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);
    room.on(RoomEvent.Disconnected, onDisconnected);

    return (): void => {
      clearGrace();
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [currentUserId, emitRecordingNotice, isLocalRecording, room]);

  const localRecordingUserIds = useMemo(() => {
    const userIds = new Set<string>();
    localRecordingDeviceKeys.forEach((deviceKey) => {
      userIds.add(parseUserIdFromDeviceKey(deviceKey));
    });
    return userIds;
  }, [localRecordingDeviceKeys]);

  const localRecordingUserIdsRef = useRef<ReadonlySet<string>>(new Set());
  localRecordingUserIdsRef.current = localRecordingUserIds;

  const isUserLocallyRecording = useCallback(
    (userId: string | null | undefined): boolean =>
      !!userId && localRecordingUserIdsRef.current.has(userId),
    [],
  );

  return {
    localRecordingState,
    localRecordingUserIds,
    isLocalRecording,
    isLocalPreparing,
    isAnyRecording,
    isUserLocallyRecording,
    error,
    startLocalRecording,
    stopLocalRecording,
  };
}
